import { useState, useRef } from 'react'
import { useMembers } from '../../../hooks/useMembers'
import { useAuth } from '../../../hooks/useAuth'
import { compressImage } from '../../../lib/imageCompression'
import { supabase } from '../../../lib/supabase'
import { Spinner } from '../../../components/ui/Spinner'
import { Portal } from '../../../components/Portal'

const uploadVehiclePhoto = async (file, householdId) => {
  const compressed = await compressImage(file, { maxWidthPx: 1200, maxHeightPx: 800, quality: 0.85 })
  const path = `${householdId}/vehicles/${crypto.randomUUID()}.jpg`
  const { error } = await supabase.storage
    .from('household-documents')
    .upload(path, compressed, { upsert: false, contentType: 'image/jpeg' })
  if (error) throw error
  return path
}

export const VehicleForm = ({ vehicle, onSave, onClose }) => {
  const { household } = useAuth()
  const { data: members = [] } = useMembers()
  const fileRef = useRef(null)

  const [year,          setYear]          = useState(vehicle?.year            ?? new Date().getFullYear())
  const [make,          setMake]          = useState(vehicle?.make            ?? '')
  const [model,         setModel]         = useState(vehicle?.model           ?? '')
  const [trim,          setTrim]          = useState(vehicle?.trim            ?? '')
  const [color,         setColor]         = useState(vehicle?.color           ?? '')
  const [licensePlate,  setLicensePlate]  = useState(vehicle?.license_plate   ?? '')
  const [vin,           setVin]           = useState(vehicle?.vin             ?? '')
  const [primaryDriver, setPrimaryDriver] = useState(vehicle?.primary_driver_id ?? '')
  const [mileage,       setMileage]       = useState(vehicle?.current_mileage ?? '')
  const [photoFile,     setPhotoFile]     = useState(null)
  const [photoPreview,  setPhotoPreview]  = useState(null)
  const [saving,        setSaving]        = useState(false)
  const [error,         setError]         = useState('')

  const realMembers = members.filter((m) => !m.isShared)

  const handlePhoto = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPhotoFile(file)
    setPhotoPreview(URL.createObjectURL(file))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!make.trim() || !model.trim()) { setError('Make and model are required.'); return }
    if (!year || year < 1900 || year > new Date().getFullYear() + 2) { setError('Enter a valid year.'); return }
    setSaving(true)
    setError('')
    try {
      let photo_path = vehicle?.photo_path ?? null
      if (photoFile) {
        photo_path = await uploadVehiclePhoto(photoFile, household.id)
      }
      await onSave({
        year:              parseInt(year),
        make:              make.trim(),
        model:             model.trim(),
        trim:              trim.trim()         || null,
        color:             color.trim()        || null,
        license_plate:     licensePlate.trim() || null,
        vin:               vin.trim()          || null,
        primary_driver_id: primaryDriver       || null,
        current_mileage:   mileage ? parseInt(mileage) : null,
        photo_path,
      })
      onClose()
    } catch (err) {
      setError(err.message ?? 'Save failed.')
      setSaving(false)
    }
  }

  const field = (label, input) => (
    <div>
      <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">{label}</label>
      {input}
    </div>
  )

  const textInput = (value, onChange, placeholder, type = 'text') => (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
    />
  )

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <form
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border flex-shrink-0">
          <h2 className="text-base font-bold text-text-primary">
            {vehicle ? 'Edit Vehicle' : 'Add Vehicle'}
          </h2>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-4">
          {/* Photo */}
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
            {photoPreview ? (
              <div className="relative">
                <img src={photoPreview} alt="" className="w-full h-36 object-cover rounded-xl" />
                <button type="button" onClick={() => { setPhotoPreview(null); setPhotoFile(null) }}
                  className="absolute top-2 right-2 bg-black/50 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">✕</button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()}
                className="w-full h-28 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-2 text-text-muted hover:border-primary hover:text-primary transition-colors">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="1" y="3" width="15" height="13" rx="2" /><path d="m16 8 5 3v5h-5" />
                  <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
                <span className="text-sm">Add photo</span>
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {field('Year', textInput(year, setYear, '2024', 'number'))}
            {field('Make', textInput(make, setMake, 'Toyota'))}
            {field('Model', textInput(model, setModel, 'Camry'))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {field('Trim', textInput(trim, setTrim, 'XSE (optional)'))}
            {field('Color', textInput(color, setColor, 'Silver (optional)'))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {field('License Plate', textInput(licensePlate, setLicensePlate, 'ABC-1234'))}
            {field('Current Mileage', textInput(mileage, setMileage, '45000', 'number'))}
          </div>

          {field('VIN', textInput(vin, setVin, '1HGCM82633A123456 (optional)'))}

          {/* Primary driver */}
          {field('Primary Driver',
            <select value={primaryDriver} onChange={(e) => setPrimaryDriver(e.target.value)}
              className="w-full rounded-xl border border-border px-3 py-2.5 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="">No primary driver</option>
              {realMembers.map((m) => (
                <option key={m.user_id} value={m.user_id}>{m.profiles?.display_name}</option>
              ))}
            </select>
          )}

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary">Cancel</button>
          <button type="submit" disabled={saving}
            className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <><Spinner size="sm" /> Saving…</> : 'Save Vehicle'}
          </button>
        </div>
      </form>
    </div>
    </Portal>
  )
}
